/**
 * Tasks — the team's jobs, manual and automatic (STORY-198, TK-D1–TK-D25).
 *
 * A task belongs to one location (TK-D16), waits on a role until somebody takes
 * it or is given straight to a person (TK-D10, TK-D11), and is open, resolved,
 * closed with a reason, or closed by DentaCore (TK-D7). **Snoozed is not a
 * status**: an open task whose `snoozedUntil` is still ahead (TK-D24).
 */

/** `WORKFLOW`: raised by a workflow's step someone does (WG-D10) — neither a kind nor a type. */
export const TASK_SOURCES = ["MANUAL", "AUTO", "WORKFLOW"] as const;
export type TaskSource = (typeof TASK_SOURCES)[number];

/** DentaCore's four automatic kinds (TK-D13). Manual types are the clinic's rows. */
export const TASK_KINDS = [
  "LOW_STOCK",
  "UNPAID_BILL",
  "RECALL_DUE",
  "VISIT_NOT_CLOSED",
  // A WhatsApp booking nobody validated within ten minutes (OQ-D21).
  "BOOKING_NOT_CHECKED",
] as const;
export type TaskKind = (typeof TASK_KINDS)[number];

export const TASK_STATUSES = ["OPEN", "RESOLVED", "CLOSED", "AUTO_CLOSED"] as const;
export type TaskStatus = (typeof TASK_STATUSES)[number];

/** Which list: open (awake), snoozed (open, sleeping), or done (anything finished). */
export const TASK_LISTS = ["open", "snoozed", "done"] as const;
export type TaskList = (typeof TASK_LISTS)[number];

/** Whose: yours and your roles' unclaimed ones, or everyone's (`tasks.view_all`, TK-D15). */
export const TASK_VIEWS = ["mine", "all"] as const;
export type TaskView = (typeof TASK_VIEWS)[number];

/**
 * The manual types every clinic starts with (TK-D14) — **content, copied into
 * the clinic's own editable rows** at sign-up and by the STORY-198a migration,
 * never read at runtime. Kept in step with that migration by hand.
 */
export const STARTER_TASK_TYPES: readonly string[] = ["Call patient", "Follow-up", "General"];

export interface TaskPerson {
  id: string;
  name: string;
}

/** What a task is about. The label is worked out when it is read. */
export interface TaskSubject {
  kind: "patient" | "item" | "invoice" | "appointment";
  id: string;
  label: string;
  /** The patient a bill or a visit belongs to, so the task can open their file (TK-D37). */
  patientId: string | null;
}

/** Who a task at one location can be given to: the clinic's roles, and the people working there. */
export interface TaskAssignable {
  roles: { id: string; name: string }[];
  people: TaskPerson[];
}

export interface TaskSummary {
  id: string;
  branchId: string;
  source: TaskSource;
  /** Set for an automatic task. */
  kind: TaskKind | null;
  /** Set for a manual task. */
  type: { id: string; name: string } | null;
  title: string;
  description: string;
  /** Who it waits on. */
  role: { id: string; name: string } | null;
  /** Who holds it. Null while it waits on its role, unclaimed. */
  assignee: TaskPerson | null;
  status: TaskStatus;
  /** ISO 8601. Ahead of now means snoozed. */
  snoozedUntil: string | null;
  dueAt: string | null;
  closeReason: string | null;
  doneAt: string | null;
  /** Null on a finished task means DentaCore finished it. */
  doneBy: TaskPerson | null;
  subject: TaskSubject | null;
  createdAt: string;
  /** Null means DentaCore created it. */
  createdBy: TaskPerson | null;
  /** Whether the reader may take it now (TK-D10). */
  canTake: boolean;
  /** Whether the reader may give it back to its role (TK-D12). */
  canGiveBack: boolean;
}

export interface TaskNoteView {
  id: string;
  kind: "NOTE" | "EVENT";
  /** Null means DentaCore wrote it. */
  author: TaskPerson | null;
  body: string;
  createdAt: string;
}

export interface TaskDetail extends TaskSummary {
  /** Oldest first. */
  notes: TaskNoteView[];
}

export interface TaskTypeView {
  id: string;
  name: string;
  archived: boolean;
  /** Tasks filed under it, finished ones included. */
  taskCount: number;
}

/**
 * One automatic kind's setting (TK-D13, TK-D22, TK-D29, TK-D32). A kind with
 * no row reads as off, with no role and no wait.
 */
export interface TaskRuleView {
  kind: TaskKind;
  enabled: boolean;
  /** When it was last switched on (TK-D29). */
  enabledAt: string | null;
  role: { id: string; name: string } | null;
  /** Visit not closed only: the visit's dentist holds it, the role takes one with none. */
  toVisitDentist: boolean;
  /** Days for Unpaid bill, hours for Visit not closed; null for the others. */
  waitAmount: number | null;
  /**
   * **Days after which its tasks read Overdue** (OQ-D9); null, the default, is
   * never — an automatic task is ordered by when it was raised.
   */
  dueDays: number | null;
}

/** The kinds that wait before raising, and in what unit. */
export const TASK_KIND_WAIT: Record<TaskKind, "days" | "hours" | null> = {
  LOW_STOCK: null,
  UNPAID_BILL: "days",
  RECALL_DUE: null,
  VISIT_NOT_CLOSED: "hours",
  // Fixed at ten minutes (OQ-D21), so nothing to set.
  BOOKING_NOT_CHECKED: null,
};

/** What each automatic kind is called on screen. */
export const TASK_KIND_LABELS: Record<TaskKind, string> = {
  LOW_STOCK: "Low stock",
  UNPAID_BILL: "Unpaid bill",
  RECALL_DUE: "Recall due",
  VISIT_NOT_CLOSED: "Visit not closed",
  BOOKING_NOT_CHECKED: "Booking not checked",
};
